import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import {
  Brain, GraduationCap, IdentificationCard, CreditCard, Books, Buildings,
  ChartLineUp, Lightning, Sparkle, ArrowUpRight, Check,
} from "@phosphor-icons/react";

const modules = [
  { icon: Lightning, name: "Lead Management" },
  { icon: GraduationCap, name: "Admissions" },
  { icon: IdentificationCard, name: "Digital Identity" },
  { icon: CreditCard, name: "Fees & Payments" },
  { icon: Buildings, name: "SIS / ERP Core" },
  { icon: Books, name: "Learning (LMS)" },
  { icon: ChartLineUp, name: "Attendance & Timetable" },
  { icon: Brain, name: "AI Proctoring" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#modules" className="hover:text-foreground">Modules</a>
            <a href="#compliance" className="hover:text-foreground">NEP 2020</a>
            <a href="#copilot" className="hover:text-foreground">AI Copilot</a>
            <a href="#pricing" className="hover:text-foreground">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" className="rounded-md" data-testid="nav-signin-btn">Sign in</Button>
            </Link>
            <Link to="/login">
              <Button className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="nav-demo-btn">
                Request demo <ArrowUpRight className="ml-1.5" size={14} weight="bold" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 eos-grid-lines opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-28 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-border bg-white px-3 py-1 rounded-full mb-6">
              <span className="accent-bar" />
              <span className="text-xs font-medium tracking-tight">NEP 2020 · APAAR · ABC · NIRF — ready</span>
            </div>
            <h1 className="font-heading font-extrabold tracking-tight text-5xl md:text-6xl lg:text-[68px] leading-[1.02]">
              The operating system<br/>
              for Indian education.
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              One identity, one record, one data model — from first inquiry to lifelong alumni.
              13 modules, no rip-and-replace, built natively for K-12 and Higher Ed.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <Link to="/login">
                <Button className="h-11 px-5 rounded-md bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]" data-testid="hero-cta-btn">
                  Open the demo workspace
                </Button>
              </Link>
              <a href="#modules">
                <Button variant="outline" className="h-11 px-5 rounded-md" data-testid="hero-explore-btn">
                  Explore 13 modules
                </Button>
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-2"><Check size={14} weight="bold" className="text-[hsl(20_90%_48%)]"/> Multi-tenant SaaS</div>
              <div className="flex items-center gap-2"><Check size={14} weight="bold" className="text-[hsl(20_90%_48%)]"/> Region-pinned data (DPDP Act)</div>
              <div className="flex items-center gap-2"><Check size={14} weight="bold" className="text-[hsl(20_90%_48%)]"/> 11 Indian languages</div>
            </div>
          </div>

          {/* Mini dashboard preview */}
          <div className="lg:col-span-5 lg:pl-6">
            <div className="eos-card rounded-lg p-0 overflow-hidden">
              <div className="px-5 py-3 border-b border-border flex items-center justify-between bg-[hsl(244_53%_20%)] text-white">
                <div className="flex items-center gap-2">
                  <Logo size={20} withText={false}/>
                  <span className="text-[11px] font-mono-eos tracking-wide opacity-80">eduos · executive view</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-white/30"/>
                  <div className="w-2 h-2 rounded-full bg-white/30"/>
                  <div className="w-2 h-2 rounded-full bg-[hsl(20_90%_48%)]"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-px bg-border">
                {[
                  { l: "Enrollment", v: "4,827", d: "+12.4%" },
                  { l: "Attendance", v: "91.2%", d: "+1.8%" },
                  { l: "Pass Rate", v: "87.6%", d: "+2.1%" },
                  { l: "Placement", v: "78.4%", d: "+6.3%" },
                ].map((k) => (
                  <div key={k.l} className="bg-white p-5">
                    <div className="eos-overline">{k.l}</div>
                    <div className="font-heading font-extrabold text-3xl mt-2 tracking-tight">{k.v}</div>
                    <div className="text-[11px] text-emerald-700 mt-1">{k.d} vs LY</div>
                  </div>
                ))}
              </div>
              <div className="p-5 border-t border-border">
                <div className="eos-overline">Fee Collection · ₹ Cr</div>
                <div className="mt-3 flex items-end gap-1.5 h-20">
                  {[18, 21, 24, 22, 26, 27, 25, 28, 29, 28].map((h, i) => (
                    <div key={`bar-${i}-${h}`} className="flex-1 bg-[hsl(244_53%_20%)]" style={{ height: `${h * 2.4}px` }}/>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section id="modules" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-5">
              <div className="eos-overline mb-3">01 · Platform</div>
              <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight leading-[1.05]">
                Thirteen modules.<br/>One canonical record.
              </h2>
            </div>
            <p className="lg:col-span-6 lg:col-start-7 text-muted-foreground leading-relaxed self-end">
              Stop reconciling spreadsheets. Every student exists once — across admissions, fees, LMS, attendance,
              exams, library, and alumni. Adopt module-by-module, never rip-and-replace.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
            {modules.map((m) => (
              <div key={m.name} className="bg-white p-6 hover:bg-[hsl(244_53%_20%)] hover:text-white transition-colors group cursor-default" data-testid={`module-${m.name}`}>
                <m.icon size={26} weight="duotone" className="text-[hsl(244_53%_20%)] group-hover:text-[hsl(20_90%_48%)]"/>
                <div className="mt-5 font-heading font-semibold text-[15px] tracking-tight">{m.name}</div>
                <div className="text-[11px] mt-1 opacity-60 group-hover:opacity-100">M0{(modules.indexOf(m) + 1).toString().padStart(1, "0")}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Copilot */}
      <section id="copilot" className="border-b border-border bg-[hsl(244_53%_20%)] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="eos-overline mb-3 text-white/60">02 · AI Layer</div>
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Ask in English.<br/>Get a board-ready answer.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-lg">
              EduOS Copilot — built on Claude Sonnet 4.5 — converts NEP outcome data, fee defaults,
              dropout-risk signals and placement trends into prescriptive recommendations.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {["Dropout-risk scoring", "Fee-default prediction", "NIRF/NAAC autopopulate"].map(x => (
                <div key={x} className="border border-white/20 rounded-md p-3 text-xs">{x}</div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="bg-[#0d0a2a] border border-white/15 rounded-md p-5 font-mono-eos text-[12.5px] leading-relaxed">
              <div className="text-white/40">› principal@iiit-h ~</div>
              <div className="mt-2">how many B.Tech CSE Y3 students are at dropout risk?</div>
              <div className="mt-4 text-white/50">copilot</div>
              <div className="mt-1 text-white">
                <span className="text-[hsl(20_90%_55%)]">▌</span> 38 students are flagged high-risk in B.Tech CSE Y3
                — 24 due to attendance &lt; 65%, 11 due to CGPA drop &gt; 1.2, and 9 with overdue fees &gt; 90d.
                Recommend mentor pairing + faculty review by Feb 28.
              </div>
              <div className="mt-3 inline-flex items-center gap-2 text-[10px] text-white/40">
                <Sparkle size={12} weight="fill"/> claude-sonnet-4-5 · 2.1s
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <h3 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">
              Built for India. Architected for the world.
            </h3>
            <p className="text-muted-foreground mt-3 max-w-2xl">
              Currently piloting with K-12 chains and tier-1 universities. Roadmap covers MENA, SE Asia and Africa.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/login">
              <Button className="h-12 px-6 rounded-md bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]" data-testid="footer-cta-btn">
                Open the workspace <ArrowUpRight className="ml-2" size={16} weight="bold" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-3"><Logo size={20} /><span>© 2026 EduOS Labs · Made for Bharat</span></div>
        <div className="flex items-center gap-5"><span>DPDP Act</span><span>GDPR</span><span>FERPA</span><span>SOC 2 (in progress)</span></div>
      </footer>
    </div>
  );
}
